import appboy from '@braze/web-sdk';
import { BrazeMessageIdGenerator, IdGenerator } from './MessageIDGenerator';
import isClient from 'Common/helpers/environment';
import { Logger, Config } from 'Common/appshell_types';
import { getCountry } from 'Common/formatting/formattingService';
import { BrazeConfig } from 'Common/types/remoteConfig';

export const BRAZE_CLIENT_CONNECTED_TO_STORE = 'BRAZE_CLIENT_CONNECTED_TO_STORE';

enum InitizliationStatus {
    notInitilized,
    loading,
    initialized,
    failed,
}

export enum BrazeEvents {
    TRIGGER_RATE_YOUR_PRODUCT = 'TRIGGER_RATE_YOUR_PRODUCT',
}
interface ActionSubsribers {
    eventType: BrazeEvents;
    callback: Function;
}
interface CustomEventArgs {
    eventName: string;
    properties: object;
}

export interface BrazeClientInterface {
    initialize: () => Promise<void>;
    logCustomEvent: (eventName: string, properties: object) => Promise<void>;
    setUser: (loginHash: string) => Promise<void>;
    logInAppMessageImpression: (messageId: string) => void;
}

type BrazeDeviceType = 'desktop' | 'mobile';

export interface BrazeClientConfig {
    device: BrazeDeviceType;
    authentication: string;
    countryCode: string;
}

interface DefaultEventProperties {
    origin: 'shop';
    device: BrazeDeviceType;
}

export const createBrazeClientConfig = (braze: BrazeConfig, config: Config, isDesktop: boolean): BrazeClientConfig => {
    return {
        authentication: isDesktop ? braze.authentication : braze.authenticationMobile,
        countryCode: config.countryCode,
        device: isDesktop ? 'desktop' : 'mobile',
    };
};

const BRAZE_MSG = 'BRAZE sending event';

export default class BrazeClient implements BrazeClientInterface {
    private status: InitizliationStatus = InitizliationStatus.notInitilized;

    private brazeApi: typeof appboy;

    private brazeUserId: string | null = null;

    private apiKey: string;

    private country: string;

    private lastSentEvent: CustomEventArgs | null = null;

    private queuedBrazeEvents: Array<{ eventName: string; customData: {} }>;

    private actionSubribers: ActionSubsribers[];

    private messagesMapper: Record<string, appboy.InAppMessage | appboy.ControlMessage>;

    private messageIdGenerator: IdGenerator;

    private defaultEventProperties: DefaultEventProperties;

    public constructor(private logger: Logger, config: BrazeClientConfig) {
        if (!isClient()) {
            this.logger.error('BrazeClient should only be used client-side');
        }

        this.defaultEventProperties = {
            origin: 'shop',
            device: config.device,
        };

        this.apiKey = config.authentication;
        this.country = getCountry(config.countryCode);
        this.messageIdGenerator = new BrazeMessageIdGenerator();
        this.actionSubribers = [];
        this.queuedBrazeEvents = [];
    }

    public async logInAppMessageImpression(messageId: string) {
        await this.initialize();
        this.brazeApi.logInAppMessageImpression(this.messagesMapper[messageId]);
        delete this.messagesMapper[messageId];
    }

    public async initialize() {
        if (this.status === InitizliationStatus.initialized || this.status === InitizliationStatus.loading) {
            return;
        }

        this.status = InitizliationStatus.loading;

        try {
            const appboy = (await import('@braze/web-sdk')).default;
            this.brazeApi = appboy;
            this.logger.debug('BRAZE initializing braze', { apiKey: this.apiKey });
            this.brazeApi.initialize(this.apiKey, {
                baseUrl: 'https://sdk.api.appboy.eu/api/v3',
                minimumIntervalBetweenTriggerActionsInSeconds: 30,
                enableHtmlInAppMessages: true,
            });

            this.status = InitizliationStatus.initialized;
            this.processQueueEvents();
            this.messagesMapper = {};
        } catch (error) {
            this.logger.error(`Fail to load appboy from braze/web-sdk`, { error: error.toString() });
            this.status = InitizliationStatus.failed;
        }
    }

    private wasEventJustSent(eventName: string, properties: object) {
        if (!this.lastSentEvent) {
            return false;
        }
        const lastEvent = this.lastSentEvent;

        if (eventName !== lastEvent.eventName) {
            return false;
        }

        if (Object.keys(properties).length !== Object.keys(lastEvent.properties).length) {
            return false;
        }

        return Object.keys(properties)
            .map(key => properties[key] === lastEvent.properties[key])
            .reduce((areAllEqual, isPairEqual) => areAllEqual && isPairEqual, true);
    }

    public async logCustomEvent(eventName: string, properties: object = {}) {
        if (this.wasEventJustSent(eventName, properties)) {
            return;
        }
        this.lastSentEvent = { eventName, properties };

        await this.initialize();

        const customData = { ...this.defaultEventProperties, ...properties };
        if (!this.brazeApi && this.status === InitizliationStatus.loading) {
            this.queuedBrazeEvents.push({ eventName, customData });
            return;
        }
        this.logger.debug(BRAZE_MSG, { eventName, customData });
        this.brazeApi.logCustomEvent(eventName, customData);
    }

    private processQueueEvents() {
        if (this.queuedBrazeEvents.length > 0 && this.brazeApi) {
            this.queuedBrazeEvents.forEach(({ eventName, customData }) => {
                this.logger.debug(BRAZE_MSG, { eventName, customData });
                this.brazeApi.logCustomEvent(eventName, customData);
            });
            this.queuedBrazeEvents = [];
        }
    }

    private onMessageReceived(message: appboy.InAppMessage | appboy.ControlMessage) {
        try {
            const assertsIsInAppMessage = (
                message: appboy.InAppMessage | appboy.ControlMessage
            ): message is appboy.InAppMessage => {
                return !!(message as appboy.InAppMessage).extras;
            };

            if (assertsIsInAppMessage(message) && message.extras.isReviewsReminderOverlay) {
                const messageId = this.messageIdGenerator.id;
                this.messagesMapper[messageId] = message;
                return this.triggerBrazeEvent(BrazeEvents.TRIGGER_RATE_YOUR_PRODUCT, {
                    messageId,
                });
            }

            return this.brazeApi.display.showInAppMessage(message);
        } catch (e) {
            this.logger.error('BRAZE had issues processing messages', e);
            return undefined;
        }
    }

    public async setUser(loginHash: string) {
        const brazeUserId = `${this.country}_${loginHash}`;
        if (this.brazeUserId === brazeUserId) {
            return;
        }

        this.brazeUserId = brazeUserId;
        await this.initialize();

        this.logger.debug('BRAZE changeUser', { brazeUserId });
        this.brazeApi.changeUser(brazeUserId);
        this.logger.debug('BRAZE subscribeToInAppMessage');
        this.brazeApi.subscribeToInAppMessage(message => this.onMessageReceived(message));
        this.logger.debug('BRAZE openning session');
        this.brazeApi.openSession();
    }

    public triggerBrazeEvent(eventType: BrazeEvents, payload?: Record<string, any>) {
        const availableSubscriber = this.actionSubribers.find(subscriber => subscriber.eventType === eventType);
        if (availableSubscriber) {
            availableSubscriber.callback(payload);
        }
    }

    public subscribeEvent(eventType: BrazeEvents, callback: Function) {
        this.actionSubribers.push({ eventType, callback });
    }
}
